import { ArrowBack } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import LoadingPattern from "components/LoadingPattern";
import { PortalContext, usePortal } from "contexts/PortalContext";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { Navigate, Outlet } from "react-router";
import { Link } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { selectForm, selectFormFields } from "redux/slices/form.slice";
import { NewGreys } from "theme/palette";
import { P } from "theme/typography";
import FormFieldProvider from "../FormFieldProvider";
import { CaseFormContext } from "contexts/CaseFormContext";
import { useContext } from "react";
import { createPortal } from "react-dom";
import { CaseDetailGroup } from "components/CaseDetailGroup";
import { selectCase } from "redux/slices/case";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { parseSafeInt } from "Backend";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { AppRoutes } from "AppRoutes";

export function CaseProfileField() {
  const { fieldId, caseId, lensId } = useParamNumbers(["fieldId", "caseId", "lensId"]);
  const intakeViewerPortal = usePortal();
  const { searchParams } = useSearchParamsWithUpdate();
  const sectionId = parseSafeInt(searchParams.get("sectionId"));

  if (fieldId === undefined || caseId === undefined || lensId === undefined) {
    return <Navigate to=".." />;
  }

  const fields = useAppSelector(selectFormFields);
  const field = fields.find((f) => f.id === fieldId);
  const caseForm = useContext(CaseFormContext);
  const form = useAppSelector(selectForm);
  const fieldValueHandle = caseForm.stateHandle[fieldId];
  const fieldValue = fieldValueHandle !== undefined ? fieldValueHandle[0] : undefined;
  const { portalRefElement } = useContext(PortalContext);
  const lensCase = useAppSelector(selectCase);

  if (field === undefined || form === undefined || fieldValue?.valueGroups === undefined) {
    return <LoadingPattern centered />;
  }

  return (
    <>
      {portalRefElement && createPortal(<CaseDetailGroup myCase={lensCase} />, portalRefElement)}
      <PortalContext.Provider value={intakeViewerPortal}>
        <Box sx={{ display: "flex", height: "100%", width: "100%", alignItems: "flex-start" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              flexShrink: 1,
              px: 2,
              minWidth: "300px",
              width: "100%",
              maxWidth: "550px", // This will limit the width to a maximum of 550px
            }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Link
                to={AppRoutes.caseProfileSection({
                  lensId,
                  caseId,
                  sectionId,
                })}>
                <IconButton data-pendo="reference-viewer-back-button">
                  <ArrowBack />
                </IconButton>
              </Link>
              <P variant="smSemiBold">
                {field.outlinePrefix || ""} {field.name}
              </P>
              <DescriptionTooltip
                description={field.description}
                sx={{
                  ml: 1,
                }}
              />
            </Box>
            <FormFieldProvider formField={field} form={form}>
              <Outlet />
            </FormFieldProvider>
          </Box>
          <Box
            ref={intakeViewerPortal.portalRef}
            sx={{
              minWidth: 60,
              flexGrow: 1,
              mt: 1,
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: NewGreys.background1,
            }}
          />
        </Box>
      </PortalContext.Provider>
    </>
  );
}
